import type dayjs from "dayjs"

import type { Attachment } from "../../Album/types/attachment.types.ts"
import type { FilesFilters } from "../types/files-filters.types.ts"
import { classifyFileFormat, type FileFormatCategory } from "./classify-file-format.ts"
import { matchesDateRange } from "./date-range-filter.ts"

interface AttachmentFilterContext {
  now?: dayjs.Dayjs
  /** The folder plus, when "include subfolders" is on, every descendant. */
  folderIds?: Set<string> | null
}

// `model_type`, `album` and `owner` are matched elsewhere (server-forwarded,
// or needing state — the album's member ids, the owner closure — that this
// pure function has no access to). This covers the dimensions an Attachment
// can be excluded from outright, or matched against its own fields alone.
export const attachmentMatchesFilters = (
  attachment: Attachment,
  filters: FilesFilters,
  { now, folderIds }: AttachmentFilterContext = {}
): boolean => {
  // Both are `document`-only concepts — an Attachment carries neither field.
  if (filters.document_type) return false
  if (filters.related_record_type && filters.related_record_id) return false

  if (filters.file_type) {
    const category: FileFormatCategory = classifyFileFormat({
      name: attachment.original_filename,
      mimeType: attachment.mime_type
    })
    if (category !== filters.file_type) return false
  }

  if (!matchesDateRange(attachment.created, filters, now)) return false

  if (filters.folder) {
    const ids = folderIds ?? new Set([filters.folder])
    if (!attachment.folder || !ids.has(attachment.folder)) return false
  }

  return true
}
